import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bookmark } from "@/lib/indexedDB";
import { Trash2, Edit3, BookmarkPlus } from "lucide-react";

const colorMap = {
  red: "bg-red-500",
  yellow: "bg-yellow-400",
  green: "bg-green-500",
  blue: "bg-blue-500",
};

interface BookmarkPanelProps {
  isOpen: boolean;
  onClose: () => void;
  bookmarks: Bookmark[];
  currentPage: number;
  onBookmarkCreate: (pageNumber: number, color?: "red" | "yellow" | "green" | "blue") => void;
  onBookmarkDelete: (id: string) => void;
  onBookmarkUpdate: (id: string, note: string, color?: "red" | "yellow" | "green" | "blue") => void;
  onBookmarkNavigate: (pageNumber: number) => void;
}

export const BookmarkPanel = ({
  isOpen,
  onClose,
  bookmarks,
  currentPage,
  onBookmarkCreate,
  onBookmarkDelete,
  onBookmarkUpdate,
  onBookmarkNavigate,
}: BookmarkPanelProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState("");
  const [editingColor, setEditingColor] = useState<"red" | "yellow" | "green" | "blue">("yellow");
  const [selectedColor, setSelectedColor] = useState<"red" | "yellow" | "green" | "blue">("yellow");

  const sortedBookmarks = [...bookmarks].sort((a, b) => a.pageNumber - b.pageNumber);

  const handleEdit = (bookmark: Bookmark) => {
    setEditingId(bookmark.id);
    setEditingNote(bookmark.note || "");
    setEditingColor(bookmark.color || "yellow");
  };

  const handleSave = (id: string) => {
    onBookmarkUpdate(id, editingNote, editingColor);
    setEditingId(null);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[300px] sm:w-[400px]">
        <SheetHeader>
          <SheetTitle>書籤</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          <div className="space-y-3">
            <Label>選擇顏色</Label>
            <div className="grid grid-cols-4 gap-2">
              {(["red", "yellow", "green", "blue"] as const).map((c) => (
                <button
                  key={c}
                  className={`h-10 rounded-md border-2 ${colorMap[c]} ${
                    selectedColor === c ? "border-foreground ring-2 ring-foreground" : "border-border"
                  }`}
                  onClick={() => setSelectedColor(c)}
                />
              ))}
            </div>
          </div>

          <Button
            onClick={() => onBookmarkCreate(currentPage, selectedColor)}
            className="w-full"
          >
            <BookmarkPlus className="mr-2 h-4 w-4" />
            新增書籤（第 {currentPage} 頁）
          </Button>

          <ScrollArea className="h-[calc(100vh-200px)]">
            <div className="space-y-2 pr-4">
              {sortedBookmarks.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-8">
                  尚無書籤
                </p>
              ) : (
                sortedBookmarks.map((bookmark) => (
                  <div
                    key={bookmark.id}
                    className="rounded-lg border bg-card p-3 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => {
                          onBookmarkNavigate(bookmark.pageNumber);
                          onClose();
                        }}
                        className="flex-1 flex items-center gap-2 text-left text-sm hover:text-primary"
                      >
                        {bookmark.color && (
                          <div className={`w-4 h-4 rounded ${colorMap[bookmark.color]}`} />
                        )}
                        <span className="font-medium">第 {bookmark.pageNumber} 頁</span>
                      </button>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(bookmark)}
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => onBookmarkDelete(bookmark.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {editingId === bookmark.id ? (
                      <div className="space-y-2">
                        <div className="space-y-2">
                          <Label className="text-xs">顏色</Label>
                          <div className="grid grid-cols-4 gap-2">
                            {(["red", "yellow", "green", "blue"] as const).map((c) => (
                              <button
                                key={c}
                                className={`h-8 rounded-md border-2 ${colorMap[c]} ${
                                  editingColor === c ? "border-foreground ring-2 ring-foreground" : "border-border"
                                }`}
                                onClick={() => setEditingColor(c)}
                              />
                            ))}
                          </div>
                        </div>
                        <Input
                          value={editingNote}
                          onChange={(e) => setEditingNote(e.target.value)}
                          placeholder="新增備註..."
                          className="text-sm"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSave(bookmark.id)}
                            className="flex-1"
                          >
                            儲存
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingId(null)}
                          >
                            取消
                          </Button>
                        </div>
                      </div>
                    ) : (
                      bookmark.note && (
                        <p className="text-xs text-muted-foreground">
                          {bookmark.note}
                        </p>
                      )
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};
